import React, { useState, useEffect, useRef } from "react";
import { Form, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons'; 
import { useTranslation } from 'react-i18next';

interface GroupItem {
  id: number;
  name: string;
}

interface GroupSelectMultiProps {
  groups: GroupItem[];
  selectedGroupIds: number[];
  onSelect: (selected: number[]) => void;
  label: string; // <<< Adicionado aqui!
}

const GroupSelectMulti: React.FC<GroupSelectMultiProps> = ({ groups, selectedGroupIds, onSelect, label }) => {
  const { t } = useTranslation(); 

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredGroups, setFilteredGroups] = useState<GroupItem[]>(groups);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const result = groups.filter((group) => group.name.toLowerCase().includes(lower));
    setFilteredGroups(result);
    setHighlightedIndex(0);
  }, [searchTerm, groups]);

  const toggleSelect = (id: number) => {
    if (selectedGroupIds.includes(id)) {
      onSelect(selectedGroupIds.filter((gid) => gid !== id));
    } else {
      onSelect([...selectedGroupIds, id]);
    }
    setSearchTerm(""); // limpa o campo após selecionar
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredGroups.length - 1));
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredGroups.length) {
        toggleSelect(filteredGroups[highlightedIndex].id);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const removeGroup = (id: number) => {
    onSelect(selectedGroupIds.filter((gid) => gid !== id));
  };

  const handleSelectAll = () => {
    const allIds = groups.map((g) => g.id);
    onSelect(allIds);
  };

  const handleClearAll = () => {
    onSelect([]);
  };

  return (
    <Form.Group style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <Form.Label style={{ marginBottom: 0 }}>{label}</Form.Label> {/* <<< Alterado */}
        <Button size="sm" variant="outline-primary" onClick={handleSelectAll}>{t("SELECT_ALL")}</Button>
        <Button size="sm" variant="outline-danger" onClick={handleClearAll}>{t("CLEAN")}</Button>
      </div>

      <Form.Control
        type="text"
        placeholder={t("TYPE_OR_SELECT_LABEL", { label })}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        ref={inputRef}
      />

      {showDropdown && (
        <div style={{
          border: "1px solid #ccc",
          maxHeight: 200,
          overflowY: "auto",
          background: "#fff",
          position: "absolute",
          zIndex: 10,
          width: inputRef.current?.offsetWidth || "100%"
        }}>
          {filteredGroups.map((group, index) => (
            <div
              key={group.id}
              onClick={() => toggleSelect(group.id)}
              style={{
                padding: 8,
                cursor: "pointer",
                backgroundColor: highlightedIndex === index ? "#e9ecef" : "white",
                fontWeight: selectedGroupIds.includes(group.id) ? "bold" : "normal"
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {group.name}
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <div style={{ padding: 8, color: "#999" }}>{t("NO_ITEMS_FOUND")}</div>
          )}
        </div>
      )}

      <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        {selectedGroupIds.map((id) => {
          const group = groups.find((g) => g.id === id);
          return group ? (
            <Badge key={id} pill bg="light" style={{ display: "flex", alignItems: "center", color: "#004085",border: "1px solid #004085" }}>
              {group.name}
              <FontAwesomeIcon
                icon={faTimes}
                style={{ marginLeft: "6px", cursor: "pointer" }}
                onClick={() => removeGroup(id)}
              />
            </Badge>
          ) : null;
        })}
      </div>
    </Form.Group>
  );
};

export default GroupSelectMulti;
